import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../Context/UserContext';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Grid,
    Chip,
    IconButton,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    TextField,
    InputAdornment
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    Category as CategoryIcon
} from '@mui/icons-material';

const mockCategories = [
    { id: 1, name: 'Outils Marketing', description: 'Applications pour le marketing digital', applicationsCount: 12, color: '#10b981' },
    { id: 2, name: 'Productivité', description: 'Outils pour améliorer la productivité', applicationsCount: 8, color: '#3b82f6' },
    { id: 3, name: 'Communication', description: 'Applications de communication', applicationsCount: 5, color: '#f59e0b' },
    { id: 4, name: 'E-commerce', description: 'Solutions de commerce électronique', applicationsCount: 15, color: '#8b5cf6' },
    { id: 5, name: 'Finances', description: 'Applications financières', applicationsCount: 3, color: '#ef4444' },
    { id: 6, name: 'Développement', description: 'Outils de développement', applicationsCount: 7, color: '#06b6d4' }
];

const CategoriesListPage = () => {
    const navigate = useNavigate();
    const { userInfo } = useContext(UserContext);
    const canManageCategories = userInfo?.is_super_admin === true || (userInfo?.permissions || []).includes('categories.manage');
    const [categories, setCategories] = useState(mockCategories);
    const [searchTerm, setSearchTerm] = useState('');

    const filteredCategories = categories.filter(category =>
        category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        category.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleAddCategory = () => {
        navigate('/categories/new');
    };

    const handleEditCategory = (categoryId) => {
        navigate(`/categories/${categoryId}/edit`);
    };

    const handleDeleteCategory = (categoryId) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ?')) {
            setCategories(prev => prev.filter(cat => cat.id !== categoryId));
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CategoryIcon sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                        Catégories d'applications
                    </Typography>
                </Box>
                {canManageCategories && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddCategory}
                    >
                        Nouvelle catégorie
                    </Button>
                )}
            </Box>

            <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                <CardContent sx={{ p: 3 }}>
                    <TextField
                        fullWidth
                        placeholder="Rechercher une catégorie..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                            )
                        }}
                        sx={{ mb: 3 }}
                    />

                    <Grid container spacing={3}>
                        {filteredCategories.map((category) => (
                            <Grid item xs={12} sm={6} md={4} key={category.id}>
                                <Card
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        boxShadow: 'none',
                                        '&:hover': {
                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                            borderColor: category.color
                                        }
                                    }}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                            <Chip
                                                label={category.name}
                                                sx={{
                                                    bgcolor: category.color,
                                                    color: 'white',
                                                    fontWeight: 600,
                                                    fontSize: '0.875rem'
                                                }}
                                            />
                                            {canManageCategories && (
                                                <Box>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleEditCategory(category.id)}
                                                        sx={{ color: 'text.secondary', mr: 1 }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDeleteCategory(category.id)}
                                                        sx={{ color: 'error.main' }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            )}
                                        </Box>

                                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                                            {category.description}
                                        </Typography>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {category.applicationsCount} application(s)
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    {filteredCategories.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <CategoryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                                Aucune catégorie trouvée
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                                {searchTerm ? 'Essayez de modifier votre recherche' : 'Commencez par créer votre première catégorie'}
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>

            <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                        Vue d'ensemble
                    </Typography>
                    <TableContainer component={Paper} sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                        <Table>
                            <TableHead sx={{ bgcolor: 'action.hover' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Catégorie</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Applications</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredCategories.map((category) => (
                                    <TableRow key={category.id} sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}>
                                        <TableCell>
                                            <Chip
                                                label={category.name}
                                                size="small"
                                                sx={{
                                                    bgcolor: category.color,
                                                    color: 'white',
                                                    fontWeight: 600
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ color: 'text.secondary' }}>
                                            {category.description}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {category.applicationsCount}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {canManageCategories && (
                                                <>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleEditCategory(category.id)}
                                                        sx={{ color: 'text.secondary', mr: 1 }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDeleteCategory(category.id)}
                                                        sx={{ color: 'error.main' }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        </Box>
    );
};

export default CategoriesListPage;